import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "@/components/shared/VenueContext";
import { hosysAiApi } from "@/api/hosysai";
import HosysAILayout from "@/components/hosysai/HosysAILayout";
import { ChevronLeft, ChevronRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_DEPARTMENTS = ["Direcció", "RRHH", "Vendes", "Màrqueting", "Atenció al client", "Operacions", "IT", "Finances", "Cuina/Producció", "Sala/Servei", "Legal", "Altres"];
const DEFAULT_PURPOSES = ["Automatització de tasques", "Redacció de textos", "Anàlisi de dades", "Atenció al client (chatbot)", "Selecció de personal", "Traduccions", "Generació de continguts", "Presa de decisions", "Detecció de patrons", "Investigació"];
const DEFAULT_DATA_TYPES = ["Dades operatives", "Comunicacions", "Currículums", "Dades de contacte", "Dades bancàries", "Dades de salut", "Origen racial/religió", "Dades biomètriques", "Ubicació", "Cap"];

const YN = [
  { value: true,  label: "Sí" },
  { value: false, label: "No" },
];

function OptionChip({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
        selected
          ? "border-cyan-400 bg-cyan-400/10 text-cyan-200"
          : "border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:text-white"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function HosysAIDiagnostic() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    sector: "", employee_count: "", organization_type: "",
    ai_tools: [], custom_tools: [], departments: [], purposes: [], data_types: [],
    uses_personal_data: null, uses_sensitive_data: null, used_in_hr: null,
    automated_decisions: null, human_supervision: null,
  });

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const [m, d] = await Promise.all([hosysAiApi.meta(), hosysAiApi.getDiagnostic(venueId)]);
      setMeta(m);
      if (d && d.completed) {
        setForm({
          sector: d.sector || "",
          employee_count: d.employee_count || "",
          organization_type: d.organization_type || "",
          ai_tools: d.ai_tools || [],
          custom_tools: d.custom_tools || [],
          departments: d.departments || [],
          purposes: d.purposes || [],
          data_types: d.data_types || [],
          uses_personal_data: d.uses_personal_data ?? null,
          uses_sensitive_data: d.uses_sensitive_data ?? null,
          used_in_hr: d.used_in_hr ?? null,
          automated_decisions: d.automated_decisions ?? null,
          human_supervision: d.human_supervision ?? null,
        });
      }
    } catch (e) {
      toast.error("Error carregant diagnòstic");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key, value) => {
    setForm((f) => {
      const arr = f[key] || [];
      const has = arr.includes(value);
      return { ...f, [key]: has ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const submit = async () => {
    setSaving(true);
    try {
      await hosysAiApi.saveDiagnostic({ venue_id: venueId, ...form });
      toast.success("Diagnòstic guardat");
      navigate("/HosysAI");
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const STEPS = [
    {
      title: "Sector",
      description: "A quin sector opera la teva organització?",
      valid: () => !!form.sector,
      render: () => (
        <div className="flex flex-wrap gap-2">
          {meta?.sectors.map((s) => (
            <OptionChip key={s} selected={form.sector === s} onClick={() => setForm({ ...form, sector: s })} testId={`sector-${s}`}>
              {s}
            </OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Mida i tipus",
      description: "Ajuda'ns a dimensionar el pla formatiu.",
      valid: () => !!form.employee_count && !!form.organization_type,
      render: () => (
        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Nombre de treballadors</p>
            <div className="flex flex-wrap gap-2">
              {["1-5", "6-15", "16-50", "51-200", "200+"].map((v) => (
                <OptionChip key={v} selected={form.employee_count === v} onClick={() => setForm({ ...form, employee_count: v })}>{v}</OptionChip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Tipus d'organització</p>
            <div className="flex flex-wrap gap-2">
              {meta?.organization_types.map((v) => (
                <OptionChip key={v} selected={form.organization_type === v} onClick={() => setForm({ ...form, organization_type: v })}>{v}</OptionChip>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Departaments",
      description: "Quins departaments participen a la teva organització?",
      valid: () => form.departments.length > 0,
      render: () => (
        <div className="flex flex-wrap gap-2">
          {DEFAULT_DEPARTMENTS.map((v) => (
            <OptionChip key={v} selected={form.departments.includes(v)} onClick={() => toggle("departments", v)}>{v}</OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Eines d'IA",
      description: "Quines eines d'IA fa servir la teva empresa (actualment o previst)?",
      valid: () => form.ai_tools.length > 0 || form.custom_tools.length > 0,
      render: () => (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {meta?.default_tools.map((v) => (
              <OptionChip key={v} selected={form.ai_tools.includes(v)} onClick={() => toggle("ai_tools", v)}>{v}</OptionChip>
            ))}
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Altres (una per línia)</p>
            <textarea
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-cyan-400"
              value={form.custom_tools.join("\n")}
              onChange={(e) => setForm({ ...form, custom_tools: e.target.value.split("\n").filter(Boolean) })}
              placeholder="Ex: eina interna, plugin personalitzat…"
            />
          </div>
        </div>
      ),
    },
    {
      title: "Finalitats",
      description: "Per a què s'utilitzen aquestes eines?",
      valid: () => form.purposes.length > 0,
      render: () => (
        <div className="flex flex-wrap gap-2">
          {DEFAULT_PURPOSES.map((v) => (
            <OptionChip key={v} selected={form.purposes.includes(v)} onClick={() => toggle("purposes", v)}>{v}</OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Dades tractades",
      description: "Quin tipus de dades es tracten amb aquestes eines?",
      valid: () => form.data_types.length > 0,
      render: () => (
        <div className="flex flex-wrap gap-2">
          {DEFAULT_DATA_TYPES.map((v) => (
            <OptionChip key={v} selected={form.data_types.includes(v)} onClick={() => toggle("data_types", v)}>{v}</OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Dades personals",
      description: "S'utilitzen dades personals identificables?",
      valid: () => form.uses_personal_data !== null,
      render: () => (
        <div className="flex gap-2">
          {YN.map((o) => (
            <OptionChip key={String(o.value)} selected={form.uses_personal_data === o.value} onClick={() => setForm({ ...form, uses_personal_data: o.value })}>
              {o.label}
            </OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Dades sensibles",
      description: "S'utilitzen dades sensibles (salut, biomètriques, religió, etc.)?",
      valid: () => form.uses_sensitive_data !== null,
      render: () => (
        <div className="flex gap-2">
          {YN.map((o) => (
            <OptionChip key={String(o.value)} selected={form.uses_sensitive_data === o.value} onClick={() => setForm({ ...form, uses_sensitive_data: o.value })}>
              {o.label}
            </OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Recursos Humans",
      description: "S'utilitza IA en processos de recursos humans (selecció, avaluació, etc.)?",
      valid: () => form.used_in_hr !== null,
      render: () => (
        <div className="flex gap-2">
          {YN.map((o) => (
            <OptionChip key={String(o.value)} selected={form.used_in_hr === o.value} onClick={() => setForm({ ...form, used_in_hr: o.value })}>
              {o.label}
            </OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Decisions automatitzades",
      description: "L'IA pren decisions que afecten a persones sense intervenció humana?",
      valid: () => form.automated_decisions !== null,
      render: () => (
        <div className="flex gap-2">
          {YN.map((o) => (
            <OptionChip key={String(o.value)} selected={form.automated_decisions === o.value} onClick={() => setForm({ ...form, automated_decisions: o.value })}>
              {o.label}
            </OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Supervisió humana",
      description: "Hi ha una persona responsable que revisa i valida les respostes de la IA?",
      valid: () => form.human_supervision !== null,
      render: () => (
        <div className="flex gap-2">
          {YN.map((o) => (
            <OptionChip key={String(o.value)} selected={form.human_supervision === o.value} onClick={() => setForm({ ...form, human_supervision: o.value })}>
              {o.label}
            </OptionChip>
          ))}
        </div>
      ),
    },
    {
      title: "Confirmar",
      description: "Revisa la informació abans de desar. Podràs modificar-la més endavant.",
      valid: () => true,
      render: () => (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-2 text-sm text-slate-200">
          <p><strong>Sector:</strong> {form.sector}</p>
          <p><strong>Mida:</strong> {form.employee_count} · {form.organization_type}</p>
          <p><strong>Departaments:</strong> {form.departments.join(", ")}</p>
          <p><strong>Eines IA:</strong> {[...form.ai_tools, ...form.custom_tools].join(", ")}</p>
          <p><strong>Finalitats:</strong> {form.purposes.join(", ")}</p>
          <p><strong>Dades:</strong> {form.data_types.join(", ")}</p>
          <p><strong>Personals:</strong> {form.uses_personal_data ? "Sí" : "No"} · <strong>Sensibles:</strong> {form.uses_sensitive_data ? "Sí" : "No"} · <strong>RRHH:</strong> {form.used_in_hr ? "Sí" : "No"} · <strong>Decisions automatitzades:</strong> {form.automated_decisions ? "Sí" : "No"} · <strong>Supervisió:</strong> {form.human_supervision ? "Sí" : "No"}</p>
        </div>
      ),
    },
  ];

  const current = STEPS[step];
  const canNext = current?.valid();
  const isLast = step === STEPS.length - 1;

  return (
    <HosysAILayout>
      <div className="space-y-6" data-testid="hosysai-diagnostic-page">
        <div>
          <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Diagnòstic IA</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Configura HOSYS AI en 12 passos</h1>
          <p className="text-slate-400 text-sm mt-1">Aquest diagnòstic ens permetrà personalitzar la formació, les polítiques i el nivell de compliance de la teva organització.</p>
        </div>

        {venueLoading || loading ? (
          <div className="h-64 flex items-center justify-center text-slate-400">Carregant…</div>
        ) : (
          <>
            {/* Progress bar */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs text-slate-400 uppercase tracking-wider">Pas {step + 1} de {STEPS.length}</p>
                <div className="flex-1" />
                <p className="text-xs text-cyan-300">{Math.round(((step + 1) / STEPS.length) * 100)}%</p>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 transition-all"
                  style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
              </div>
            </div>

            {/* Step */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 min-h-[300px]">
              <h2 className="text-2xl font-bold text-white">{current.title}</h2>
              <p className="text-sm text-slate-400 mt-1 mb-6">{current.description}</p>
              {current.render()}
            </div>

            {/* Nav */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 flex items-center gap-1 text-sm font-medium disabled:opacity-30"
                data-testid="diag-prev"
              >
                <ChevronLeft className="w-4 h-4" /> Anterior
              </button>
              <div className="flex-1" />
              {!isLast ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canNext}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold flex items-center gap-1 text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="diag-next"
                >
                  Següent <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-500 text-white font-semibold flex items-center gap-1 text-sm disabled:opacity-50"
                  data-testid="diag-submit"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Desar diagnòstic
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </HosysAILayout>
  );
}
